import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getSuppliersList } from '../services'
import {
    createSupplierColumns,
    createSupplierFilters,
    SUPPLIER_FILTER_DEFAULTS,
    type SupplierFilters,
} from '../utils/suppliers-config'

/**
 * Business logic hook for Suppliers List
 * Manages data fetching, pagination, sorting, and filtering
 */
export const useSuppliers = () => {
    const { t } = useTranslation('contract')

    // URL params management
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<SupplierFilters>({
        defaults: { sort_by: 'partner_id', sort_order: 'desc', page_size: 20 },
        filterDefaults: SUPPLIER_FILTER_DEFAULTS,
    })

    // Form for filter controls
    const { control } = useForm<SupplierFilters>({
        defaultValues: filters,
    })

    // Fetch suppliers data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SUPPLIERS, queryParams],
        queryFn: () => getSuppliersList(queryParams),
        placeholderData: true,
    })

    // Memoized columns & filters
    const columns = useMemo(() => createSupplierColumns(t), [t])
    const filterItems = useMemo(() => createSupplierFilters(t), [t])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        suppliers: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        control,
        setFilter,
        searchInputProps,

        // Configs
        columns,
        filterItems,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}

export default useSuppliers
